import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { LD } from './tokens';

interface ListingDetailRowProps {
  icon: React.ReactNode;
  title: string;
  /** The row's answer, readable without opening it. */
  subtitle: React.ReactNode;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  /** Desktop rows sit flush with the column; phone rows carry the page gutter. */
  inset?: boolean;
  /** Render the body only once the row is first opened. */
  lazy?: boolean;
  testId?: string;
  children: React.ReactNode;
}

/**
 * One collapsed row of the listing dialog: icon tile, title, and the answer
 * as a subtitle, opening in place to the full section.
 */
const ListingDetailRow: React.FC<ListingDetailRowProps> = ({
  icon,
  title,
  subtitle,
  expanded,
  onChange,
  inset = true,
  lazy = false,
  testId,
  children,
}) => (
  <Accordion
    expanded={expanded}
    onChange={(_, isExpanded) => onChange(isExpanded)}
    disableGutters
    square
    elevation={0}
    TransitionProps={lazy ? { mountOnEnter: true } : undefined}
    data-testid={testId}
    sx={{
      bgcolor: 'white',
      borderBottom: `1px solid ${LD.line}`,
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMore sx={{ fontSize: 24, color: LD.muted2 }} />}
      sx={{
        px: inset ? 2 : 0,
        py: '11px',
        minHeight: 0,
        '&.Mui-expanded': { minHeight: 0 },
        '& .MuiAccordionSummary-content': {
          my: 0,
          alignItems: 'center',
          gap: '12px',
          minWidth: 0,
        },
        '& .MuiAccordionSummary-content.Mui-expanded': { my: 0 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 36,
          flex: '0 0 36px',
          borderRadius: '9px',
          bgcolor: LD.navyTint,
          color: LD.navy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& svg': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: '15.5px',
            color: LD.ink,
            lineHeight: 'normal',
          }}
        >
          {title}
        </Typography>
        <Typography
          component="div"
          sx={{
            fontSize: '13px',
            color: LD.muted,
            mt: '1px',
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 0, pb: '12px' }}>{children}</AccordionDetails>
  </Accordion>
);

export default ListingDetailRow;
